import { useState } from 'react';
import { Edit2, Trash2, ChevronRight, Search, Eye } from 'lucide-react';
import { Dropdown } from '../ui/Dropdown';
import type { Account } from '../../types/accounting';

interface AccountListProps {
  accounts: Account[];
  onEdit: (account: Account) => void;
  onDelete: ((account: Account) => void) | undefined;
  onSelect?: (account: Account) => void;
  selectedAccount?: Account | null;
  loading?: boolean;
}

const accountTypeConfig = {
  asset: { label: 'Actif', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  liability: { label: 'Passif', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20' },
  equity: { label: 'Capitaux', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-50 dark:bg-purple-900/20' },
  revenue: { label: 'Produits', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20' },
  expense: { label: 'Charges', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20' }
};

export function AccountList({ 
  accounts, 
  onEdit, 
  onDelete, 
  onSelect,
  selectedAccount,
  loading 
}: AccountListProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Protection pour les données undefined/null
  const safeAccounts = Array.isArray(accounts) ? accounts.filter(account => account != null && account.code && account.name) : [];
  
  // Filtrage local pour recherche instantanée
  const filteredAccounts = safeAccounts.filter(account => 
    account.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Barre de recherche discrète */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un compte..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
        />
      </div>

      {/* Liste des comptes */}
      <div className="space-y-1 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
        {filteredAccounts.length === 0 ? (
          <div className="text-center py-12 text-gray-400 dark:text-gray-500">
            <p className="text-sm">Aucun compte trouvé</p>
          </div>
        ) : (
          filteredAccounts.map((account) => {
            const isSelected = selectedAccount?.id === account.id;
            const isHovered = hoveredId === account.id;
            const typeConfig = accountTypeConfig[account.type];

            return (
              <div
                key={account.id}
                onClick={() => onSelect?.(account)}
                onMouseEnter={() => setHoveredId(account.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`
                  group relative p-3 rounded-lg cursor-pointer
                  transition-all duration-200 ease-out
                  ${isSelected 
                    ? 'bg-primary/10 border-l-4 border-primary shadow-sm' 
                    : 'bg-white dark:bg-gray-800 border-l-4 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  }
                  ${isHovered && !isSelected ? 'shadow-md transform scale-[1.02]' : 'shadow-sm'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className={`
                        text-xs font-mono font-semibold px-2 py-0.5 rounded
                        ${isSelected ? 'bg-primary/20 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}
                      `}>
                        {account.code}
                      </code>
                      <span className={`
                        text-xs px-2 py-0.5 rounded-full font-medium
                        ${typeConfig.color} ${typeConfig.bgColor}
                      `}>
                        {typeConfig.label}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {account.name}
                    </p>
                  </div>

                  {/* Actions avec dropdown */}
                  <div className={`
                    flex items-center gap-1 ml-2
                    transition-opacity duration-200
                    ${isHovered || isSelected ? 'opacity-100' : 'opacity-0'}
                  `}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown
                      items={[
                        {
                          label: 'Voir',
                          icon: Eye,
                          onClick: () => onSelect?.(account),
                          variant: 'primary'
                        },
                        {
                          label: 'Modifier',
                          icon: Edit2,
                          onClick: () => onEdit(account),
                          separator: onDelete ? true : false
                        },
                        ...(onDelete ? [{
                          label: 'Supprimer',
                          icon: Trash2,
                          onClick: () => onDelete(account),
                          variant: 'danger' as const
                        }] : [])
                      ]}
                      align="right"
                    />
                  </div>

                  {/* Indicateur de sélection */}
                  <ChevronRight className={`
                    h-4 w-4 ml-2 transition-all duration-200
                    ${isSelected 
                      ? 'text-primary opacity-100 transform translate-x-0' 
                      : 'text-gray-400 opacity-0 transform -translate-x-2'
                    }
                  `} />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Compteur discret */}
      {filteredAccounts.length > 0 && (
        <div className="text-xs text-gray-400 dark:text-gray-500 text-center pt-2 border-t border-gray-100 dark:border-gray-800">
          {filteredAccounts.length} compte{filteredAccounts.length > 1 ? 's' : ''}
          {searchTerm && ` sur ${safeAccounts.length}`}
        </div>
      )}
    </div>
  );
}